/**
 * GateFinalScreen — Quality Gate 5 · Final (Aceite da Entrega).
 *
 * Aceite CONJUNTO Negócio (PO) + Técnica (Arq. de Soluções) sobre a entrega, antes
 * do PR seguir. Fiel ao protótipo, com DADOS REAIS: resumo do Pull Request e os 11
 * Engineering Standards (via GET /executions/{id}/output), aderência da Conferência
 * de Entrega (delivery-check) e a aprovação dupla real (PATCH /journey-gates/final
 * com role business|technical → aprovado só quando ambos aceitam). Lê ?project=.
 */
import { MOSTRAR_ENGINEERING_STANDARDS } from '@/lib/engineeringStandards';
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useJourneyGate, useDecideJourneyGate } from '@/api/techScope';
import { useDeliveryCheck, useLatestExecution } from '@/api/deliveryCheck';
import { useExecutionOutput } from '@/api/executions';
import { GateExportBar } from './GateExportBar';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

const GATE_DEF = {
  num: 5, label: 'Aceite da Entrega', sla: '48h',
  personas: 'Negócio (PO) + Técnica (Arq. de Soluções)',
  approvers: [
    { name: 'Maria Souza', role: 'Product Owner', avatar: 'MS', side: 'Negócio', role_key: 'business' },
    { name: 'Carlos Oliveira', role: 'Arquiteto de Soluções', avatar: 'CO', side: 'Técnica', role_key: 'technical' },
  ],
};

// Descrição legível dos 11 Engineering Standards (por slug do compliance check).
const STD_DESC = {
  'oop+solid': 'OOP + princípios SOLID', 'oop': 'Orientação a objetos',
  ddd: 'Domain-Driven Design', tdd: 'Test-Driven Development', clean: 'Clean Code',
  togaf: 'Arquitetura corporativa (TOGAF)', atam: 'Architecture Tradeoff Analysis',
  nfr: 'Requisitos não-funcionais', sdlc: 'SDLC Seguro', owasp: 'OWASP ASVS',
  bsa: 'Licenciamento (BSA)', safecode: 'Secure coding (SAFECode)',
};

// Resultado do compliance check → rótulo/cor (tolera nomes de campo variados).
function stdMeta(check) {
  const res = String(check.result ?? check.status ?? '').toLowerCase();
  if (check.has_waiver || res === 'waived') return { label: 'RISCO ACEITO', color: C.quantum, icon: '🛡' };
  if (res === 'passed' || res === 'pass' || res === 'ok') return { label: 'VERIFICADO', color: C.cyber, icon: '✓' };
  if (res === 'warning' || res === 'warn') return { label: 'ATENÇÃO', color: C.digital, icon: '!' };
  if (res === 'skipped' || res === 'skip') return { label: 'PULADO', color: C.muted, icon: '–' };
  return { label: 'FALHOU', color: C.hot, icon: '✗' };
}

// O title de cada PR multi-repo já vem prefixado com o nome curto do repo:
// "[orquestrador-de-cotacao-backend] feat: …". Separa o repo do restante do título.
function splitRepoTitle(title) {
  const m = /^\s*\[([^\]]+)\]\s*(.*)$/.exec(title || '');
  return m ? { repo: m[1], rest: m[2] } : { repo: null, rest: title || '' };
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: 4, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function Card({ title, icon, accent = C.cyber, right, children }) {
  return (
    <div style={{ marginTop: 16, border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ color: accent }}>{icon}</span>
        <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>{title}</h3>
        {right && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: C.muted }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

export function GateFinalScreen({ projectId: propProjectId, onContinue, onBack } = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = propProjectId || urlProject || projects?.[0]?.id || null;
  const project = projects.find((p) => p.id === projectId) || null;

  const { data: gate } = useJourneyGate(projectId, 'final');
  const decide = useDecideJourneyGate(projectId, 'final');
  const { data: delivery } = useDeliveryCheck(projectId);
  const { data: latestExec } = useLatestExecution(projectId);
  const executionId = latestExec?.executionId || null;
  const { data: output } = useExecutionOutput(executionId);
  const [comment, setComment] = useState('');

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const accepts = gate?.accepts || {};
  const bAcc = Boolean(accepts.business);
  const tAcc = Boolean(accepts.technical);
  const accepted = gate?.status === 'approved' || (bAcc && tAcc);
  const collecting = (bAcc || tAcc) && !accepted;
  const badge = accepted
    ? { label: 'ENTREGA ACEITA', color: C.cyber }
    : collecting ? { label: 'COLETANDO ACEITES', color: C.digital }
      : { label: 'AGUARDANDO ACEITE', color: C.digital };

  const rows = delivery?.rows ?? [];
  const okReqs = rows.filter((r) => r.status === 'atendido').length;
  const divergences = rows.filter((r) => r.status !== 'atendido' || r.divergence);
  const criteriaTotal = delivery?.criteriaTotal ?? 0;
  const adherence = criteriaTotal > 0 ? (delivery?.coveragePct ?? 0) : (delivery?.verdictPct ?? 0);
  const adhColor = adherence >= 90 ? C.cyber : adherence >= 50 ? C.digital : C.hot;

  const prs = output?.pull_requests || [];          // multi-repo (Bloco C): 1 PR por repo
  const multiPr = prs.length > 1;
  const pr = output?.pull_request || prs[0] || null; // âncora (single ou 1º de N)
  const artifacts = output?.code_artifacts ?? [];
  const filesChanged = output?.artifacts_total ?? artifacts.length;
  const adds = multiPr
    ? prs.reduce((a, p) => a + (p.additions || 0), 0)
    : (pr?.additions ?? artifacts.reduce((a, x) => a + (x.additions || 0), 0));
  const dels = multiPr
    ? prs.reduce((a, p) => a + (p.deletions || 0), 0)
    : (pr?.deletions ?? artifacts.reduce((a, x) => a + (x.deletions || 0), 0));
  const checks = output?.compliance_checks ?? [];
  const stdOk = checks.filter((c) => {
    const m = stdMeta(c); return m.label === 'VERIFICADO' || m.label === 'RISCO ACEITO';
  }).length;

  const acceptSide = (role_key) => decide.mutate({ decision: 'approve', role: role_key, comment });
  const goDelivery = () => navigate(`/execution/delivery-check?project=${projectId}`);
  const goPR = () => { if (pr?.pr_url || pr?.url) window.open(pr.pr_url || pr.url, '_blank', 'noopener'); };
  const goFinish = () => (onContinue ? onContinue() : navigate(`/project?project=${projectId}`));

  // ── objeto normalizado para exportação (.md / PDF) — GateExportBar ─────────
  const exportGate = {
    num: GATE_DEF.num,
    label: GATE_DEF.label,
    projectName: project?.name,
    projectId,
    description: 'Aceite conjunto de Negócio + Técnica sobre todas as fases e entregáveis '
      + 'antes de o Pull Request seguir. Consolida a aderência da entrega (Conferência) e o '
      + (MOSTRAR_ENGINEERING_STANDARDS ? 'resumo do PR e os 11 Engineering Standards.' : 'resumo do PR.'),
    status: accepted ? 'approved' : (collecting ? 'collecting' : 'pending'),
    statusLabel: badge.label,
    decidedAt: gate?.decidedAt,
    comment: gate?.comment,
    sla: GATE_DEF.sla,
    personas: GATE_DEF.personas,
    approvers: GATE_DEF.approvers.map((a) => ({
      ...a, done: a.role_key === 'business' ? bAcc : tAcc,
    })),
    sections: [
      multiPr ? {
        title: 'Pull Requests (multi-repo)',
        kind: 'table',
        headers: ['Repo', '#', 'Título', 'Branch', '+', '−'],
        rows: prs.map((p) => {
          const { repo, rest } = splitRepoTitle(p.title);
          return [repo || '—', p.number ?? '—', rest || 'Pull Request da execução',
            p.branch || p.head_branch || '—', p.additions ?? 0, p.deletions ?? 0];
        }),
      } : (pr ? {
        title: 'Pull Request',
        kind: 'kv',
        data: [
          ['Número', `#${pr.number ?? '—'}`],
          ['Título', pr.title || 'Pull Request da execução'],
          ['Branch', pr.branch || pr.head_branch || '—'],
          ['Commit', pr.commit_sha ? String(pr.commit_sha).slice(0, 10) : '—'],
          ['Alterações', `+${adds} · −${dels} · ${filesChanged} arquivos`],
          ['URL', pr.pr_url || pr.url || '—'],
        ],
      } : null),
      (MOSTRAR_ENGINEERING_STANDARDS && checks.length) ? {
        title: 'Boas práticas verificadas · Engineering Standards',
        kind: 'table',
        headers: ['Standard', 'Descrição', 'Status', 'Evidência'],
        rows: checks.map((c) => {
          const m = stdMeta(c);
          const code = String(c.standard ?? c.rule_name ?? '').trim();
          const desc = STD_DESC[code.toLowerCase()] || c.detail || '';
          const ev = c.evidence || c.detail || '';
          return [code || '—', desc || '—', m.label, ev || '—'];
        }),
      } : null,
      rows.length ? {
        title: 'Conferência de Entrega',
        kind: 'kv',
        data: [
          ['Aderência', `${adherence}%`],
          ['Requisitos atendidos', `${okReqs}/${rows.length}`],
          ['Divergências', String(divergences.length)],
        ],
      } : null,
      divergences.length ? {
        title: 'Divergências da Conferência de Entrega',
        kind: 'list',
        items: divergences.map((r) => `${r.requirement}${r.divergence?.text ? ` — ${r.divergence.text}` : ''}`),
      } : null,
    ].filter(Boolean),
  };

  return (
    <div className="sp-page" style={{ color: C.text, maxWidth: 1040 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>
          🛡 Quality Gate {GATE_DEF.num} — {GATE_DEF.label}
        </h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 10px', borderRadius: 6,
          color: badge.color, background: `${badge.color}1F`, border: `1px solid ${badge.color}55`,
          letterSpacing: '0.04em' }}>{badge.label}</span>
        <GateExportBar gate={exportGate} style={{ marginLeft: 'auto' }} />
      </div>
      <p style={{ color: C.dim, marginTop: 8, fontSize: 13.5, maxWidth: '85ch' }}>
        Aceite conjunto de <strong style={{ color: C.text }}>Negócio + Técnica</strong> sobre todas as fases
        e entregáveis antes de o Pull Request seguir. Consolida a aderência da entrega (Conferência)
        {MOSTRAR_ENGINEERING_STANDARDS ? ', o resumo do PR e os 11 Engineering Standards.' : ' e o resumo do PR.'}
      </p>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
        <Kpi label="Aderência da entrega" value={rows.length ? `${adherence}%` : '—'} color={adhColor}
          sub="Conferência de Entrega" />
        <Kpi label="Requisitos atendidos" value={rows.length ? `${okReqs}/${rows.length}` : '—'} color={C.cyber}
          sub={`${divergences.length} divergência${divergences.length !== 1 ? 's' : ''}`} />
        <Kpi label="Arquivos alterados" value={filesChanged || '—'} color={C.cyber}
          sub={(adds || dels) ? `+${adds} · −${dels}` : 'artefatos da execução'} />
        {MOSTRAR_ENGINEERING_STANDARDS && (
          <Kpi label="Engineering Standards" value={checks.length ? `${stdOk}/${checks.length}` : '—'} color={C.cyber}
            sub="verificados com evidência" />
        )}
      </div>

      {gate?.decidedAt && (
        <p style={{ color: C.muted, fontSize: 12.5, marginTop: 12 }}>
          Aceite conjunto registrado em {new Date(gate.decidedAt).toLocaleString('pt-BR')}
          {gate.comment ? ` · ${gate.comment}` : ''}
        </p>
      )}
      {decide.isError && (
        <div style={{ marginTop: 12, color: C.hot, fontSize: 13 }}>
          {String(decide.error?.title || decide.error?.message || decide.error)}
        </div>
      )}

      {/* Pull Request */}
      <Card title={multiPr ? 'Pull Requests' : 'Pull Request'} icon="⑃"
        right={multiPr
          ? `${prs.length} PRs · 1 por repositório`
          : (pr ? (pr.is_simulated ? 'simulado (sandbox)' : 'PR real') : null)}>
        {multiPr ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {prs.map((p, i) => {
              const { repo, rest } = splitRepoTitle(p.title);
              const url = p.pr_url || p.url;
              return (
                <div key={p.pr_id || p.number || i} style={{ padding: '10px 12px', background: C.black,
                  borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: C.cyber, fontWeight: 700,
                      background: `${C.cyber}18`, border: `1px solid ${C.cyber}44`, borderRadius: 6, padding: '2px 8px' }}>
                      {repo || 'repo'}
                    </span>
                    <span style={{ fontFamily: 'monospace', fontSize: 14, color: C.cyber, fontWeight: 700 }}>
                      #{p.number ?? '—'}
                    </span>
                    <span style={{ fontSize: 13, color: C.text, flex: 1, minWidth: 160 }}>
                      {rest || 'Pull Request da execução'}
                    </span>
                    {url && (
                      <button onClick={() => window.open(url, '_blank', 'noopener')} style={{ cursor: 'pointer',
                        background: 'transparent', color: C.cyber, border: `1px solid ${C.cyber}55`, borderRadius: 7,
                        padding: '5px 11px', fontSize: 12 }}>
                        Ver Pull Request →
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: C.dim }}>
                    <span>🌿 <span style={{ fontFamily: 'monospace' }}>{p.branch || p.head_branch || '—'}</span></span>
                    {p.commit_sha && <span>commit <span style={{ fontFamily: 'monospace', color: C.text }}>{String(p.commit_sha).slice(0, 10)}</span></span>}
                    <span style={{ color: C.cyber }}>+{p.additions ?? 0}</span>
                    <span style={{ color: C.hot }}>−{p.deletions ?? 0}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : pr ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 14, color: C.cyber, fontWeight: 700 }}>
                #{pr.number ?? '—'}
              </span>
              <span style={{ fontSize: 13.5, color: C.text, flex: 1, minWidth: 200 }}>{pr.title || 'Pull Request da execução'}</span>
              {(pr.pr_url || pr.url) && (
                <button onClick={goPR} style={{ cursor: 'pointer', background: 'transparent', color: C.cyber,
                  border: `1px solid ${C.cyber}55`, borderRadius: 7, padding: '5px 11px', fontSize: 12 }}>
                  Ver Pull Request →
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10, fontSize: 12, color: C.dim }}>
              <span>🌿 <span style={{ fontFamily: 'monospace' }}>{pr.branch || pr.head_branch || '—'}</span></span>
              {pr.commit_sha && <span>commit <span style={{ fontFamily: 'monospace', color: C.text }}>{String(pr.commit_sha).slice(0, 10)}</span></span>}
              <span style={{ color: C.cyber }}>+{adds}</span>
              <span style={{ color: C.hot }}>−{dels}</span>
              <span>{filesChanged} arquivos</span>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            O Pull Request é criado/atualizado pela execução (Fase 4). Rode a execução até a geração de
            código para ver o resumo do PR aqui.
          </div>
        )}
      </Card>

      {/* Engineering Standards */}
      {MOSTRAR_ENGINEERING_STANDARDS && (
      <Card title="Boas práticas verificadas · Engineering Standards" icon="🛡"
        right={checks.length ? `${stdOk}/${checks.length} verificados` : null}>
        {checks.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Os 11 Engineering Standards são verificados na execução (Gate B / compliance). Sem checks
            registrados ainda para a última execução.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8 }}>
            {checks.map((c, i) => {
              const m = stdMeta(c);
              const code = String(c.standard ?? c.rule_name ?? '').trim();
              const desc = STD_DESC[code.toLowerCase()] || c.detail || '';
              const ev = c.evidence || c.detail || '';
              return (
                <div key={c.check_id || i} style={{ padding: 10, background: C.black, borderRadius: 8,
                  border: `1px solid ${C.border}`, borderLeft: `3px solid ${m.color}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: m.color }}>{m.icon}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>{code || '—'}</span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 8.5, color: m.color,
                      background: `${m.color}1F`, borderRadius: 4, padding: '1px 6px' }}>{m.label}</span>
                  </div>
                  {desc && <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>{desc}</div>}
                  {ev && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4, fontFamily: 'monospace' }}>Evidência: {ev}</div>}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      )}

      {/* Conferência de Entrega (aderência + divergências) */}
      <Card title="Conferência de Entrega" icon="✔" accent={C.quantum}
        right={<button onClick={goDelivery} style={{ cursor: 'pointer', background: 'transparent', color: C.cyber,
          border: `1px solid ${C.cyber}55`, borderRadius: 7, padding: '4px 10px', fontSize: 11.5 }}>Ver Conferência →</button>}>
        {rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Sem conferência gerada. Rode a Conferência de Entrega antes do aceite final.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: C.dim, marginBottom: 8 }}>
              Aderência <strong style={{ color: adhColor }}>{adherence}%</strong> · {okReqs}/{rows.length} requisitos atendidos.
            </div>
            {divergences.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {divergences.slice(0, 5).map((r, i) => (
                  <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: `${C.digital}0A`,
                    border: `1px solid ${C.digital}44`, fontSize: 12, color: C.dim }}>
                    <span style={{ color: C.digital }}>⚠ {r.requirement}</span>
                    {r.divergence?.text ? ` — ${r.divergence.text}` : ''}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Aceite duplo */}
      <div style={{ marginTop: 20, border: `1px solid ${accepted ? `${C.cyber}55` : `${C.digital}44`}`,
        background: C.surface2, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.05em', color: C.muted, fontWeight: 700, marginBottom: 10 }}>
          ACEITE CONJUNTO · {GATE_DEF.personas.toUpperCase()} · SLA {GATE_DEF.sla}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          {GATE_DEF.approvers.map((a) => {
            const done = a.role_key === 'business' ? bAcc : tAcc;
            return (
              <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 8,
                border: `1px solid ${done ? C.cyber : C.border}`, borderRadius: 999,
                padding: '4px 12px 4px 4px', background: C.black }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: done ? C.cyber : C.surface2,
                  color: done ? C.black : C.dim, display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700 }}>{a.avatar}</span>
                <span style={{ fontSize: 12 }}>
                  <span style={{ color: C.text, fontWeight: 600 }}>{a.name}</span>
                  <span style={{ color: C.muted }}> · {a.side}</span>
                </span>
                <span style={{ color: done ? C.cyber : C.muted, fontSize: 12 }}>{done ? '✓' : '○'}</span>
              </div>
            );
          })}
        </div>

        {!accepted && (
          <>
            <label style={{ fontSize: 12.5, color: C.dim }}>Parecer / observações do aceite (obrigatório para rejeitar)</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
              placeholder="Ex.: divergência do barramento de eventos aceita — será tratada na próxima sprint…"
              style={{ width: '100%', marginTop: 6, background: C.black, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'transparent', color: C.dim, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>← Voltar</button>
          )}
          {accepted ? (
            <>
              <span style={{ color: C.cyber, fontSize: 13, fontWeight: 600 }}>✓ Entrega aceita — commit liberado</span>
              <button onClick={goFinish} style={{ marginLeft: 'auto', background: C.cyber, color: C.black,
                border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {pr?.pr_url || pr?.url ? 'Ver Pull Request →' : 'Concluir jornada →'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => acceptSide('business')} disabled={decide.isPending || bAcc}
                style={{ background: bAcc ? C.surface : C.cyber, color: bAcc ? C.muted : C.black, border: 'none',
                  borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
                  cursor: bAcc ? 'default' : 'pointer', opacity: decide.isPending ? 0.6 : 1 }}>
                {bAcc ? 'Negócio ✓' : 'Aceitar (Negócio)'}
              </button>
              <button onClick={() => acceptSide('technical')} disabled={decide.isPending || tAcc}
                style={{ background: tAcc ? C.surface : C.quantum, color: tAcc ? C.muted : C.neutral, border: 'none',
                  borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700,
                  cursor: tAcc ? 'default' : 'pointer', opacity: decide.isPending ? 0.6 : 1 }}>
                {tAcc ? 'Técnica ✓' : 'Aceitar (Técnica)'}
              </button>
              <button onClick={() => decide.mutate({ decision: 'reject', comment })} disabled={decide.isPending}
                style={{ background: 'transparent', color: C.hot, border: `1px solid ${C.hot}`, borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button>
              {collecting && (
                <span style={{ marginLeft: 'auto', fontSize: 12, color: C.digital }}>Aguardando o segundo aceite…</span>
              )}
            </>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10 }}>
          O aceite conjunto gera evento imutável na trilha de auditoria (aderência, divergências e
          justificativas). O commit fica liberado somente após Negócio + Técnica aceitarem.
        </div>
      </div>
    </div>
  );
}
